import { useGameStore } from '@/store/gameStore'
import { GameIcon } from '../icons/iconMap'
import {
  STAFF_DEFINITIONS, STAFF_SKILL_DEFS, StaffType,
} from '@/config/staffConfig'
import { getFacilityDef } from '@/config/facilityRegistry'
import { isFeatureUnlocked, CLUB_LEVELS } from '@/config/clubLevelConfig'
import { PlayerAvatar } from '../components/PlayerAvatar'
import { staffPortraitSrc } from '../helpers/portraits'
import { SectionDeck, StatStrip, EmptyStateRich } from '../components/CourtVision'
import { useConfirm } from '../components/useConfirm'
import { Users, UserPlus, Lock } from 'lucide-react'

function skillColor(value: number): string {
  if (value >= 70) return 'var(--victory-orange)'
  if (value >= 50) return 'var(--gold)'
  return 'var(--text-secondary)'
}

/** Compact per-axis skill bars (the sub-skill breakdown of a member) */
function SkillBars({ type, skills }: { type: StaffType; skills: Record<string, number> }) {
  const defs = STAFF_SKILL_DEFS[type] ?? []
  if (defs.length === 0) return null
  return (
    <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
      {defs.map(def => {
        const value = skills?.[def.key] ?? 0
        return (
          <div key={def.key} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', minWidth: 130, flex: 1 }}>
            <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', width: 62, flexShrink: 0 }}>
              {def.label}
            </span>
            <div className="progress-track" style={{ flex: 1 }}>
              <div className="progress-fill angled" style={{ width: `${value}%`, background: skillColor(value) }} />
            </div>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: skillColor(value), width: 20, textAlign: 'right' }}>
              {value}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export function StaffTabContent() {
  const { money, staff, hireStaff, removeStaff, hasBuilding, clubLevel } = useGameStore()
  const { confirm, confirmDialog } = useConfirm()

  const handleFire = async (staffId: string, staffName: string) => {
    if (await confirm({
      title: 'Despedir empleado',
      message: <>¿Despedir a <b>{staffName}</b>? Dejará de prestar sus servicios en el club.</>,
      confirmLabel: 'Despedir',
      confirmTone: 'danger',
    })) {
      removeStaff(staffId)
    }
  }

  const getStaffCount = (type: StaffType) => staff.filter(s => s.type === type).length

  const canHire = (type: StaffType) => {
    const def = STAFF_DEFINITIONS[type]
    if (def.feature && !isFeatureUnlocked(def.feature, clubLevel)) return false
    if (def.requires && !hasBuilding(def.requires)) return false
    if (getStaffCount(type) >= def.maxCount) return false
    return money >= def.baseSalary
  }

  const totalSalary = staff.reduce((acc, s) => acc + s.salary, 0)

  return (
    <div>
      {confirmDialog}
      <SectionDeck title="En nómina" icon={Users} meta={`${staff.length} empleado${staff.length === 1 ? '' : 's'}`}>
        {staff.length === 0 ? (
          <EmptyStateRich
            icon={Users}
            title="El club funciona solo... de momento"
            sub="Contrata personal para mejorar la atención, la demanda y el mantenimiento"
          />
        ) : (
          <>
            <div style={{ marginBottom: '0.6rem' }}>
              <StatStrip
                label="Nómina semanal"
                value={<span className="money-negative">-{totalSalary.toLocaleString('es-ES')}€</span>}
              />
            </div>
            <div className="space-y-2">
              {staff.map((s) => (
                <div key={s.id} className="row-tile" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.3rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <PlayerAvatar id={s.id} name={s.name} size={36} src={s.avatarId ? staffPortraitSrc(s.avatarId) : undefined} />
                    <div className="row-tile-main">
                      <div className="row-tile-title">{s.name}</div>
                      <div className="row-tile-sub">
                        {STAFF_DEFINITIONS[s.type].name} · Skill {s.skill}% · Moral {s.happiness}%
                      </div>
                    </div>
                    <div className="row-tile-side">
                      <span className="money-negative" style={{ fontWeight: 700 }}>
                        {s.salary.toLocaleString('es-ES')}€/sem
                      </span>
                      <button
                        onClick={() => handleFire(s.id, s.name)}
                        className="btn-danger-ghost"
                        style={{ fontSize: '0.7rem' }}
                      >
                        Despedir
                      </button>
                    </div>
                  </div>
                  <SkillBars type={s.type} skills={s.skills} />
                </div>
              ))}
            </div>
          </>
        )}
      </SectionDeck>

      <SectionDeck title="Contratar" icon={UserPlus}>
        <div className="space-y-2">
          {Object.values(STAFF_DEFINITIONS).map((def) => {
            const count = getStaffCount(def.type)
            const missingBuilding = def.requires && !hasBuilding(def.requires)
            const featureLocked = def.feature && !isFeatureUnlocked(def.feature, clubLevel)
            const atCap = count >= def.maxCount

            if (featureLocked) {
              const unlockLevel = CLUB_LEVELS.find(l => l.unlocks.includes(def.feature!))
              return (
                <div key={def.type} className="row-tile" style={{ opacity: 0.6 }}>
                  <span className="icon-tile tone-muted"><GameIcon domain="staff" id={def.type} size={18} /></span>
                  <div className="row-tile-main">
                    <div className="row-tile-title">{def.name}</div>
                    <div className="row-tile-sub">{def.description}</div>
                  </div>
                  <span className="pill pill-warning">
                    <Lock size={10} /> Nv.{unlockLevel?.level}: {unlockLevel?.name}
                  </span>
                </div>
              )
            }

            if (missingBuilding) {
              return (
                <div key={def.type} className="row-tile" style={{ opacity: 0.6 }}>
                  <span className="icon-tile tone-muted"><GameIcon domain="staff" id={def.type} size={18} /></span>
                  <div className="row-tile-main">
                    <div className="row-tile-title">{def.name}</div>
                    <div className="row-tile-sub">{def.description}</div>
                  </div>
                  <span className="pill pill-warning">
                    <Lock size={10} /> Requiere {getFacilityDef(def.requires!)?.name ?? def.requires}
                  </span>
                </div>
              )
            }

            return (
              <button
                key={def.type}
                onClick={() => hireStaff(def.type)}
                disabled={!canHire(def.type)}
                className="row-tile"
              >
                <span className="icon-tile tone-cta"><GameIcon domain="staff" id={def.type} size={18} /></span>
                <div className="row-tile-main">
                  <div className="row-tile-title">
                    {def.name}
                    {count > 0 && <span className="pill pill-info">{count}/{def.maxCount} en plantilla</span>}
                  </div>
                  <div className="row-tile-sub">{def.description}</div>
                </div>
                <div className="row-tile-side">
                  {atCap ? (
                    <span className="pill pill-info">Plantilla completa</span>
                  ) : (
                    <span className={canHire(def.type) ? 'money-positive' : 'money-negative'} style={{ fontWeight: 700 }}>
                      {def.baseSalary.toLocaleString('es-ES')}€/sem
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </SectionDeck>
    </div>
  )
}
